import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps, Grid } from 'semantic-ui-react';
import styled from 'styled-components';
import { FormikProps } from 'formik';

import { FieldLabel } from '../../../../../styles';
import { listProperties, PropertyWalletItemDTO } from '../../../../../services/properties';
import { maskZipCode } from '../../../../../services/masks';
import { CreateClaimEstateInitialInitialValuesType } from '../Formik/types/CreateClaimEstateInitialValuesTypes';

type Props = Pick<FormikProps<CreateClaimEstateInitialInitialValuesType>, 'setFieldValue' | 'values'> & {
  estateId?: string;
  hideLabel?: boolean;
  label?: string;
  placeholder?: string;
  compact?: boolean;
};

const getAddressLabel = (item: PropertyWalletItemDTO): string => {
  if (item?.address?.formatted) return item.address.formatted;

  return [
    item?.address?.street,
    item?.address?.number,
    item?.address?.complement,
    item?.address?.neighborhood,
    item?.address?.city,
    item?.address?.state
  ]
    .filter(Boolean)
    .join(', ');
};

export const WalletTag = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  color: #2185d0;
  margin-bottom: 0.5rem;
`;

const ClaimPropertyWalletSearchDropdown = ({
  estateId,
  setFieldValue,
  values,
  hideLabel = false,
  label = 'PESQUISAR NA CARTEIRA DE IMÓVEIS',
  placeholder = 'Busque por CEP, bairro, logradouro ou código do imóvel',
  compact = false
}: Props): ReactElement | null => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const requestIdRef = useRef(0);

  const selectedPublicId = values?.claimEstate?.propertyPublicId;

  useEffect(() => {
    if (!estateId) {
      setItems([]);
      return;
    }

    const timeout = setTimeout(async () => {
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      setIsLoading(true);

      try {
        const response = await listProperties({
          estateId,
          search: search.trim() || undefined,
          page: 1,
          limit: 10
        });

        if (currentRequestId === requestIdRef.current) {
          setItems(response.items || []);
        }
      } catch (_error) {
        if (currentRequestId === requestIdRef.current) {
          setItems([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [estateId, search]);

  const itemsByPublicId = useMemo(() => {
    const hash = new Map<string, PropertyWalletItemDTO>();
    items.forEach(item => {
      if (item?.publicId) hash.set(item.publicId, item);
    });
    return hash;
  }, [items]);

  const options: DropdownItemProps[] = items.map(item => {
    const addressLabel = getAddressLabel(item);
    const identification = item.identification || 'Sem código do imóvel';

    return {
      key: item.publicId,
      value: item.publicId,
      // Endereço como texto principal: com códigos UUID, o filtro local do SUI
      // quebrava a busca por CEP/bairro/logradouro (só olhava o `text`).
      text: addressLabel || identification,
      description: addressLabel ? identification : undefined
    };
  });

  return (
    <Grid.Row
      style={compact ? { paddingTop: 0, paddingBottom: 0 } : undefined}
    >
      <Grid.Column width={16}>
        {!hideLabel && <FieldLabel>{label}</FieldLabel>}
        <Dropdown
          fluid
          selection
          clearable
          // Busca é server-side; identity evita o filtro local por `text` do Semantic UI.
          search={(opts: DropdownItemProps[]) => opts}
          loading={isLoading}
          options={options}
          value={selectedPublicId || ''}
          noResultsMessage="Nenhum imóvel encontrado."
          placeholder={placeholder}
          onSearchChange={(_event, data: DropdownProps) => {
            setSearch(String(data.searchQuery || ''));
          }}
          onChange={(_event, data: DropdownProps) => {
            const selectedId = String(data.value || '');

            if (!selectedId) {
              setFieldValue('claimEstate.propertyPublicId', undefined);
              setFieldValue('claimProperty.propertyCode', '');
              setFieldValue('claimProperty.zipCode', '');
              setFieldValue('claimProperty.street', '');
              setFieldValue('claimProperty.district', '');
              setFieldValue('claimProperty.city', '');
              setFieldValue('claimProperty.state', '');
              setFieldValue('claimProperty.number', '');
              setFieldValue('claimProperty.complement', '');
              return;
            }

            const selected = itemsByPublicId.get(selectedId);
            if (!selected) return;

            setFieldValue('claimEstate.propertyPublicId', selected.publicId);
            setFieldValue('claimProperty.propertyCode', selected.identification || '');
            setFieldValue('claimProperty.zipCode', maskZipCode(selected?.address?.zipcode || ''));
            setFieldValue('claimProperty.street', selected?.address?.street || '');
            setFieldValue('claimProperty.district', selected?.address?.neighborhood || '');
            setFieldValue('claimProperty.city', selected?.address?.city || '');
            setFieldValue('claimProperty.state', selected?.address?.state || '');
            setFieldValue('claimProperty.number', selected?.address?.number || '');
            setFieldValue('claimProperty.complement', selected?.address?.complement || '');
            setFieldValue('claimProperty.country', 'BR');
          }}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

export default ClaimPropertyWalletSearchDropdown;
